/**
 * `/r/<token>` — the page a payment request's "Give now" link opens (GH-845).
 *
 * ## What it says, and what it never says
 *
 * An organization asked this person to give: the amount, what it is for, and the organization's
 * own note, then the ordinary give box. It is a **request to give** — the page says so in its own
 * sentence, and never calls it a bill, an invoice or an amount due. A pledge statement thanks the
 * person for the promise and names what is left of it.
 *
 * ## What the server decides
 *
 * Everything but the amount the donor chooses. The link carries a signed request id; the page asks
 * the server what it opens. An open request comes back with the remaining amount and a freshly
 * signed checkout context, and the checkout sends the link again with every quote. The amount opens
 * at what is still asked and the donor may change it (GH-849): the server checks it ($1 or more),
 * prices the gift, and credits the request with at most what it still asks. A paid, cancelled or
 * lapsed request comes back without a context, and the page offers the organization's own page
 * instead: "you can still give".
 */
import { useEffect, useState } from "react";
import { formatMoney, type PublicPaymentRequestResponse } from "@givehere/contracts";
import { ApiError, fetchPaymentRequest } from "./api.js";
import { Checkout } from "./Checkout.js";

/** The sentence every request carries (the domain's `REQUEST_VOLUNTARY_LINE`, word for word). */
export const VOLUNTARY_LINE =
  "This is a request, not a bill. Every gift is voluntary, and you are free to give a different " +
  "amount or not at all.";

/** GH-849 (owner, 2026-10-02): part of what was asked, or more, from $1. */
export const DIFFERENT_AMOUNT_LINE =
  "The amount below is what was asked. You are welcome to change it — any gift of $1.00 or more.";

const formatDue = (dueOn: string): string =>
  new Date(`${dueOn}T12:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

export function PaymentRequestPage({ token }: { token: string }) {
  const [page, setPage] = useState<PublicPaymentRequestResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPaymentRequest(token).then(
      (loaded) => {
        if (!cancelled) setPage(loaded);
      },
      (caught: unknown) => {
        if (!cancelled) {
          setFailure(
            caught instanceof ApiError ? caught.message : "This link could not be opened.",
          );
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (failure) {
    return (
      <main className="card">
        <h1>This link is not valid</h1>
        <p className="notice">{failure}</p>
        <a className="home-link" href="/">
          Find an organization to give to
        </a>
      </main>
    );
  }

  if (!page) {
    return (
      <main className="card">
        <p className="muted" role="status">
          Opening the request…
        </p>
      </main>
    );
  }

  const organizationPage = `/o/${encodeURIComponent(page.organization.id)}`;

  if (page.state !== "open") {
    const heading =
      page.state === "paid"
        ? "This request has been met — thank you"
        : page.reason === "unavailable"
          ? `${page.organization.displayName} can't take gifts here right now`
          : "This request is no longer active";
    const canGive = page.state === "paid" || page.canGive;
    return (
      <main className="card">
        <h1 dir="auto">{heading}</h1>
        {canGive ? (
          <>
            <p>
              You can still give to <bdi>{page.organization.displayName}</bdi> whenever you like.
            </p>
            <a className="home-link" href={organizationPage}>
              Give to <bdi>{page.organization.displayName}</bdi>
            </a>
          </>
        ) : (
          <a className="home-link" href="/">
            Find another organization
          </a>
        )}
      </main>
    );
  }

  return (
    <>
      <section className="card" aria-label="Payment request">
        <h1 dir="auto">
          {page.isPledgeStatement ? (
            <>
              Your pledge to <bdi>{page.organization.displayName}</bdi>
            </>
          ) : (
            <>
              A giving request from <bdi>{page.organization.displayName}</bdi>
            </>
          )}
        </h1>
        <p>
          {page.isPledgeStatement
            ? `Thank you for your pledge. ${formatMoney(page.amount)} of it remains to be given`
            : `${page.organization.displayName} is asking whether you would consider a gift of ${formatMoney(page.amount)}`}
          {page.designationName ? ` toward ${page.designationName}` : ""}.
        </p>
        {page.note && (
          <blockquote dir="auto">
            <p>{page.note}</p>
          </blockquote>
        )}
        {page.dueOn && (
          <p className="muted">
            If you can, they would be grateful to receive it by {formatDue(page.dueOn)}.
          </p>
        )}
        <p className="muted small">{VOLUNTARY_LINE}</p>
        <p className="muted small">{DIFFERENT_AMOUNT_LINE}</p>
      </section>
      <Checkout
        contextToken={page.contextToken}
        paymentRequest={{ token, amountMinor: page.amount.amountMinor }}
      />
    </>
  );
}
